import { CrudConfig, handleIPTypeChange } from '../../../../model/table';
import { IP } from '../../../../dtos/version.dto';
import { useStores } from '../../../store';
import {ActionType, AddressIPType, AddressType, IPDirection} from '../../../../utils/enums';
import { isDuplicate } from '../../../../utils/validationUtils';
import {
    buildCreateIpRequestBody,
    buildDeleteIpRequestBody,
    buildUpdateIpRequestBody
} from '../../../../utils/builderReqUtility';
import fwRulesService from '../../../../services/fw-rules.service';


const ipListDataChangesHandler = {
    set: (obj: any, prop: string, value: any) => {
        handleIPTypeChange(obj, prop, value);
        obj[prop] = value;
        return true;
    }
};

const getEndpoint = (actionType: ActionType) =>
    actionType === ActionType.ALLOW ? 'allow-list' : 'block-list';

export const useIpListCrudConfig = (): CrudConfig<IP> => {
    const { fwVersionStore } = useStores();

    return {
        createNewText: 'Add IP Address',
        displayInlineActions: !!fwVersionStore.isSelectedVersionEditable,
        disableEditReason: fwVersionStore.getDisableReason,
        newRecord: {
            value: '',
            description: '',
            id: '',
            displayName: '',
            type: AddressType.IPV4_NETMASK,
            ipType: AddressIPType.IP_NETMASK
        },
        isDuplicated: (record: IP, originalRecord: IP | undefined, keys: string[]): boolean => {
            if (record.direction !== IPDirection.BOTH && originalRecord?.direction !== IPDirection.BOTH) {
                return isDuplicate(record, originalRecord, fwVersionStore.getIpList, keys);
            }

            // custom check for BOTH direction — handles overlap between BOTH/INBOUND/OUTBOUND
            if (record.direction === IPDirection.BOTH) {
                const originalInbound  = originalRecord ? { ...originalRecord, direction: IPDirection.INTBOUND } : undefined;
                const originalOutbound = originalRecord ? { ...originalRecord, direction: IPDirection.OUTBOUND } : undefined;

                return isDuplicate({ ...record, direction: IPDirection.INTBOUND },  originalInbound,  fwVersionStore.getIpList, keys) ||
                    isDuplicate({ ...record, direction: IPDirection.OUTBOUND }, originalOutbound, fwVersionStore.getIpList, keys);
            }

            const adjustedOriginal = { ...originalRecord, direction: record.direction };
            return isDuplicate(record, adjustedOriginal, fwVersionStore.getIpList, keys);
        },
        saveCb: async (data: IP) => {
            const reqBody = buildCreateIpRequestBody(data);
            await fwVersionStore.genericCrudMethod(fwRulesService.createIp, [reqBody, getEndpoint(data.actionType)]);
        },
        updateCb: async (updatedRecord: IP, originalRecord: IP) => {
            // when actionType changes, newActionRule in the body tells the backend which list to create
            const reqBody = buildUpdateIpRequestBody(updatedRecord, originalRecord);
            await fwVersionStore.genericCrudMethod(fwRulesService.updateIp, [reqBody, getEndpoint(originalRecord.actionType)]);
        },
        removeCb: async (data: IP) => {
            const reqBody = buildDeleteIpRequestBody(data);
            await fwVersionStore.genericCrudMethod(fwRulesService.deleteIp, [reqBody, getEndpoint(data.actionType)]);
        },
        dataChangesHandler: ipListDataChangesHandler,
        entityName: 'IP Address'
    };
};